import type { OsWindow } from "@/components/ui/os-layout/OsContext";
import type { OsAniSnapshotSource } from "@/components/ui/os-layout/buildOsAniContextSnapshot";
import { getAgentEntityDescriptor, type AgentEntityKind } from "@/services/agent/agentNavigation";

export type FeOpenExpectation =
  | { kind: "task-review"; taskId: string }
  | {
      kind: "app";
      appId: string;
      focusKey?: string;
      /** When set, only a window with this param (e.g. caseId) counts — not a list-only singleton. */
      detailParamKey?: string;
      detailParamValue?: string;
    };

export type FeOpenVerification = {
  ok: boolean;
  windowId?: string;
  reason?: string;
};

function normalizeTaskId(raw: unknown): string {
  return String(raw ?? "").trim().toLowerCase();
}

function normalizeEntityId(raw: unknown): string {
  return String(raw ?? "").trim().toLowerCase();
}

function windowTaskId(params: Record<string, string>): string {
  return normalizeTaskId(params.taskId ?? params.task_id);
}

const DETAIL_PARAM_ALIASES: Record<string, readonly string[]> = {
  caseId: ["caseId", "case_id"],
  meetingId: ["meetingId", "meeting_id"],
  missionId: ["missionId", "mission_id"],
  taskId: ["taskId", "task_id", "task"],
  artifactId: ["artifactId", "artifact_id"],
  id: ["id", "artifactId", "artifact_id", "portfolioId", "portfolio_id", "productId", "product_id"],
};

function windowDetailParam(
  params: Record<string, string>,
  key: string,
): string {
  const aliases = DETAIL_PARAM_ALIASES[key] ?? [key];
  for (const alias of aliases) {
    const value = String(params[alias] ?? "").trim();
    if (value) return value;
  }
  return "";
}

export function detailExpectationForEntity(
  entityKind: AgentEntityKind,
  id: string,
): FeOpenExpectation | null {
  const descriptor = getAgentEntityDescriptor(entityKind);
  const detail = descriptor?.detail;
  if (!detail) {
    return null;
  }
  const appId = detail.detailAppId ?? descriptor?.appId;
  if (!appId) {
    return null;
  }
  return {
    kind: "app",
    appId,
    detailParamKey: detail.windowParamKey,
    detailParamValue: id,
  };
}

export function listExpectationForEntity(entityKind: AgentEntityKind, id: string): FeOpenExpectation | null {
  const descriptor = getAgentEntityDescriptor(entityKind);
  if (!descriptor?.listHighlight) {
    return null;
  }
  return { kind: "app", appId: descriptor.appId, focusKey: descriptor.listHighlight.focusKey(id) };
}

function windowsInActiveWorkspace(source: OsAniSnapshotSource): OsWindow[] {
  const live = source.getWindowsSnapshot?.();
  if (live) {
    return live.filter((w) => w.workspaceId === source.activeWorkspaceId);
  }
  const windowIds = source.windowIdsByWorkspace.get(source.activeWorkspaceId) ?? [];
  return windowIds
    .map((id) => source.getWindow(id))
    .filter((w): w is OsWindow => Boolean(w));
}

function findMatchingWindow(
  source: OsAniSnapshotSource,
  expectation: FeOpenExpectation,
) {
  const windows = windowsInActiveWorkspace(source);

  for (let index = windows.length - 1; index >= 0; index -= 1) {
    const window = windows[index];
    if (!window || window.isMinimized) continue;

    if (expectation.kind === "task-review") {
      if (
        window.appId === "task-review" &&
        windowTaskId(window.params) === normalizeTaskId(expectation.taskId)
      ) {
        return window;
      }
      continue;
    }

    if (window.appId !== expectation.appId) {
      continue;
    }

    if (expectation.detailParamKey && expectation.detailParamValue) {
      const windowValue =
        expectation.detailParamKey === "id"
          ? windowDetailParam(window.params, "id") ||
            windowDetailParam(window.params, "artifactId")
          : windowDetailParam(window.params, expectation.detailParamKey);
      if (
        normalizeEntityId(windowValue) !== normalizeEntityId(expectation.detailParamValue)
      ) {
        continue;
      }
    }

    if (expectation.focusKey) {
      const windowFocus = String(window.params.focus ?? "").trim();
      if (windowFocus !== expectation.focusKey) {
        continue;
      }
    }

    return window;
  }

  return undefined;
}

function maximizedBlocker(
  source: OsAniSnapshotSource,
  targetWindowId: string,
): string | undefined {
  for (const window of windowsInActiveWorkspace(source)) {
    if (window?.isMaximized && window.id !== targetWindowId) {
      return window.id;
    }
  }
  return undefined;
}

export function verifyFeOpenInSnapshot(
  source: OsAniSnapshotSource,
  expectation: FeOpenExpectation,
): FeOpenVerification {
  const window = findMatchingWindow(source, expectation);
  if (!window) {
    return { ok: false, reason: "No matching window found in the OS" };
  }

  const blockerId = maximizedBlocker(source, window.id);
  if (blockerId) {
    return {
      ok: false,
      windowId: window.id,
      reason: "Window opened behind a maximized window",
    };
  }

  // Agent opens from the Ani sidebar — chat keeps browser focus; a visible window is enough.
  return { ok: true, windowId: window.id };
}

export async function waitForFeOpenVerified(
  readSnapshot: () => OsAniSnapshotSource,
  expectation: FeOpenExpectation,
  options?: { attempts?: number; delayMs?: number },
): Promise<FeOpenVerification> {
  const attempts = options?.attempts ?? 6;
  const delayMs = options?.delayMs ?? 120;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const verification = verifyFeOpenInSnapshot(readSnapshot(), expectation);
    if (verification.ok) return verification;
    if (attempt < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    } else {
      return verification;
    }
  }

  return { ok: false, reason: "Timed out waiting for window" };
}
